import { Injectable, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CartActions } from '@next/core-lib/cart';
import { AbstractDialogResultData, DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';

import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { UserActions } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { ProductWizardTab } from 'src/app/product/models/product.models';

import * as AppActions from './actions';
import * as ProductActions from '../../product/store/actions';
import { RemarksComponent } from '../components/cart/components/column-remarks/remarks/remarks.component';
import { CartSnackbarComponent } from '../components/cart-snackbar/cart-snackbar.component';
import { ConfirmDialogData } from '../models/app.models';

@Injectable()
export class CartEffects {
  openSiloDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openSiloDialog),
      switchMap(({ cartIdentifier, cartLine }) =>
        of(
          ProductActions.openWizardDialogForExistingCartLine({
            cartId: cartIdentifier,
            cartLine,
            initialSelectedTab: ProductWizardTab.SILOS,
          }),
        ),
      ),
    ),
  );

  openDeliveryDateDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openDeliveryDateDialog),
      switchMap(({ cartIdentifier, cartLine, orderType }) =>
        of(
          ProductActions.openWizardDialogForExistingCartLine({
            cartId: cartIdentifier,
            cartLine,
            initialSelectedTab: ProductWizardTab.DELIVERY_INFORMATION,
            orderType,
          }),
        ),
      ),
    ),
  );

  openProductSupplementDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openProductSupplementDialog),
      switchMap(({ cartIdentifier, cartLine }) =>
        of(
          ProductActions.openWizardDialogForExistingCartLine({
            cartId: cartIdentifier,
            cartLine,
            initialSelectedTab: ProductWizardTab.PRODUCT_SUPPLEMENTS,
          }),
        ),
      ),
    ),
  );

  deleteLineFromCartConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteLineFromCartConfirmation),
      switchMap((props) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('cart.delete-cart-line-title'),
            text: this.localeService.translate('cart.delete-cart-line-text'),
            confirmButtonText: this.localeService.translate('remove'),
          })
          .pipe(switchMap((result) => (result?.status === DIALOG_RETURN_TYPES.confirm ? of(CartActions.deleteCartLineFromCart({ ...props })) : EMPTY))),
      ),
    ),
  );

  deleteCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.deleteCartConfirmation),
      switchMap((props) => {
        const hasReturnUrl = props.returnUrl?.length > 0;
        const baseDialogProperties = {
          title: this.localeService.translate('cart.delete-cart-title'),
          text: this.localeService.translate('cart.delete-cart-text'),
        };

        const formatDialogResult = (result: AbstractDialogResultData<ConfirmDialogData>) =>
          result?.status === DIALOG_RETURN_TYPES.confirm ? of(CartActions.deleteCartRequest({ ...props })) : EMPTY;

        if (hasReturnUrl) {
          const applicationName = `cart.cart-${props.returnApplicationNameKey || 'cart.cart-genericAppName'}`;
          const confirmButtonText = `${this.localeService.translate('cart.cart-delete-and-return-to')} ${this.localeService.translate(applicationName)}`;
          return this.dialogService
            .confirm({
              ...baseDialogProperties,
              confirmButtonText,
            })
            .pipe(switchMap((result) => formatDialogResult(result)));
        } else {
          return this.dialogService
            .confirmWarning({
              ...baseDialogProperties,
              confirmButtonText: this.localeService.translate('remove'),
            })
            .pipe(switchMap((result) => formatDialogResult(result)));
        }
      }),
    ),
  );

  simulateSuccessful$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.patchSimulateCartRequestSuccess),
        tap(({ cartId }) => {
          this.ngZone.run(() => this.router.navigate(['cart/confirm', cartId]));
        }),
      ),
    { dispatch: false },
  );

  finalizeSuccessful$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.patchFinalizeCartRequestSuccess),
        tap(({ cartId }) => this.ngZone.run(() => this.router.navigate(['cart/thank-you', cartId]))),
      ),
    { dispatch: false },
  );

  instantOrderSuccessful$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CartActions.patchInstantOrderSuccess),
        switchMap(() =>
          this.dialogService.notify({
            cancelButton: false,
            title: this.localeService.translate('cart-confirmation.instant-order'),
            text: this.localeService.translate('cart-confirmation.instant-order-notification-description'),
            confirmButtonText: this.localeService.translate('cart-confirmation.acknowledge'),
            confirmButton: true,
            pushHistoryState: false,
          }),
        ),
        tap(() => this.ngZone.run(() => this.router.navigate(['']))),
      ),
    { dispatch: false },
  );

  changeCustomer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.locationChanged, UserActions.locationInitialized),
        withLatestFrom(this.store.select(RouterSelectors.getPath)),
        tap(([_, routeData]) => {
          if (routeData.startsWith('/confirm') || routeData.startsWith('/thank-you')) {
            this.ngZone.run(() => this.router.navigate(['']));
          }
        }),
      ),
    { dispatch: false },
  );

  openRemarksDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.openRemarksDialog),
      switchMap((actionData) =>
        this.dialogService.fullscreenDialog(
          RemarksComponent,
          {
            cartId: actionData.cartId,
            cartLine: actionData.cartLine,
            cancelButton: true,
          },
          {
            autoFocus: true,
          },
        ),
      ),
      map(() => AppActions.openRemarksDialogDialogSuccess()),
    ),
  );

  errorHappened$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.errorHappened),
        tap((props) => {
          this._snackbar.openFromComponent(CartSnackbarComponent, {
            data: props?.translatedValidationMessages,
          });
        }),
      ),
    { dispatch: false },
  );

  constructor(
    protected actions$: Actions,
    private readonly store: Store,
    private readonly dialogService: DialogService,
    private readonly localeService: LocaleService,
    private readonly router: Router,
    private ngZone: NgZone,
    private readonly _snackbar: MatSnackBar,
  ) {}
}
